import React, { useState, useEffect, useRef } from 'react';
import { useSeasonalTheme } from '../../contexts/SeasonalThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { manageUserCredits } from '../../services/usersApi';

// Helper function to check if user has scratched today
const hasScatchedToday = () => {
  const lastScratch = localStorage.getItem('blackFridayLastScratch');
  if (!lastScratch) return false;

  const lastScratchDate = new Date(lastScratch);
  const today = new Date();

  return (
    lastScratchDate.getDate() === today.getDate() &&
    lastScratchDate.getMonth() === today.getMonth() &&
    lastScratchDate.getFullYear() === today.getFullYear()
  );
};

// Save today's scratch
const markAsScratched = () => {
  localStorage.setItem('blackFridayLastScratch', new Date().toISOString());
};

// Check if badge is dismissed for today
const isDismissedToday = () => {
  const dismissed = localStorage.getItem('blackFridayDismissedToday');
  if (!dismissed) return false;

  const dismissedDate = new Date(dismissed);
  const today = new Date();

  return (
    dismissedDate.getDate() === today.getDate() &&
    dismissedDate.getMonth() === today.getMonth() &&
    dismissedDate.getFullYear() === today.getFullYear()
  );
};

// Check if badge is permanently dismissed
const isPermanentlyDismissed = () => {
  return localStorage.getItem('blackFridayDismissedPermanently') === 'true';
};

// Dismiss for today
const dismissForToday = () => {
  localStorage.setItem('blackFridayDismissedToday', new Date().toISOString());
};

// Dismiss permanently
const dismissPermanently = () => {
  localStorage.setItem('blackFridayDismissedPermanently', 'true');
};

// Calculate countdown to Black Friday
const getCountdown = () => {
  const blackFriday = new Date('2025-11-28T00:00:00'); // Black Friday 2025
  const now = new Date();
  const diff = blackFriday - now;

  if (diff < 0) {
    // Black Friday has passed, show days until end
    const endDate = new Date('2025-12-01T23:59:59');
    const endDiff = endDate - now;
    if (endDiff < 0) return null; // Event ended

    const days = Math.floor(endDiff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((endDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    return `Ends in ${days}d ${hours}h`;
  }

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  return `${days}d ${hours}h`;
};

// Scratch Card Modal Component
const ScratchCardModal = ({ onClose, onRewardClaimed }) => {
  const canvasRef = useRef(null);
  const [isScratching, setIsScratching] = useState(false);
  const [scratchProgress, setScratchProgress] = useState(0);
  const [isRevealed, setIsRevealed] = useState(false);
  const [particles, setParticles] = useState([]);
  const [reward, setReward] = useState(null);
  const [isClaiming, setIsClaiming] = useState(false);

  // Generate random reward
  useEffect(() => {
    const rewards = [
      { type: 'credits', amount: 5000, label: '5,000 Free Credits', emoji: '💰', rarity: 'common' },
      { type: 'credits', amount: 10000, label: '10,000 Free Credits', emoji: '💰💰', rarity: 'common' },
      { type: 'credits', amount: 25000, label: '25,000 Free Credits!', emoji: '🎉', rarity: 'rare' },
      { type: 'credits', amount: 50000, label: '50,000 Free Credits!!', emoji: '🔥', rarity: 'epic' },
      { type: 'credits', amount: 100000, label: '100,000 FREE CREDITS!!!', emoji: '🌟', rarity: 'legendary' },
      { type: 'dud', label: 'Better Luck Tomorrow!', emoji: '💔', rarity: 'dud' },
    ];

    const random = Math.random();
    let selectedReward;

    // 50% common (5,000-10,000 credits)
    // 30% rare (25,000 credits)
    // 15% epic (50,000 credits)
    // 4% legendary (100,000 credits)
    // 1% dud

    if (random < 0.5) {
      selectedReward = rewards[Math.random() < 0.5 ? 0 : 1]; // 5,000 or 10,000 credits
    } else if (random < 0.8) {
      selectedReward = rewards[2]; // 25,000 credits
    } else if (random < 0.95) {
      selectedReward = rewards[3]; // 50,000 credits
    } else if (random < 0.99) {
      selectedReward = rewards[4]; // 100,000 credits
    } else {
      selectedReward = rewards[5]; // dud
    }

    setReward(selectedReward);
  }, []);

  const rarityStyles = {
    common: {
      bg: 'linear-gradient(135deg, #C0C0C0 0%, #909090 100%)',
      border: '#808080',
      glow: '0 0 20px rgba(192, 192, 192, 0.5)',
    },
    rare: {
      bg: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
      border: '#DAA520',
      glow: '0 0 30px rgba(255, 215, 0, 0.8)',
    },
    epic: {
      bg: 'linear-gradient(135deg, #9945FF 0%, #14F195 100%)',
      border: '#9945FF',
      glow: '0 0 40px rgba(153, 69, 255, 0.9)',
    },
    legendary: {
      bg: 'linear-gradient(135deg, #FF00FF 0%, #00FFFF 50%, #FFD700 100%)',
      border: '#FF00FF',
      glow: '0 0 50px rgba(255, 0, 255, 1)',
      animation: 'rainbow 3s ease infinite',
    },
    dud: {
      bg: 'linear-gradient(135deg, #4a4a4a 0%, #2a2a2a 100%)',
      border: '#666666',
      glow: '0 0 10px rgba(74, 74, 74, 0.3)',
    },
  };

  const style = rarityStyles[reward?.rarity] || rarityStyles.common;

  // Initialize canvas
  useEffect(() => {
    if (!reward) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();

    canvas.width = rect.width * 2;
    canvas.height = rect.height * 2;
    ctx.scale(2, 2);

    // Draw scratch coating based on rarity
    const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);

    if (reward.rarity === 'legendary') {
      gradient.addColorStop(0, '#FF00FF');
      gradient.addColorStop(0.5, '#00FFFF');
      gradient.addColorStop(1, '#FFD700');
    } else if (reward.rarity === 'epic') {
      gradient.addColorStop(0, '#9945FF');
      gradient.addColorStop(1, '#14F195');
    } else if (reward.rarity === 'rare') {
      gradient.addColorStop(0, '#FFD700');
      gradient.addColorStop(1, '#FFA500');
    } else {
      gradient.addColorStop(0, '#C0C0C0');
      gradient.addColorStop(1, '#909090');
    }

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, rect.width, rect.height);

    // Add sparkle texture
    ctx.fillStyle = 'rgba(255, 255, 255, 0.2)';
    for (let i = 0; i < 100; i++) {
      ctx.fillRect(
        Math.random() * rect.width,
        Math.random() * rect.height,
        Math.random() * 2 + 1,
        Math.random() * 2 + 1
      );
    }
  }, [reward]);

  const scratch = (e) => {
    const canvas = canvasRef.current;
    if (!canvas || isRevealed) return;

    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();

    // Get correct coordinates accounting for canvas scaling
    const clientX = e.clientX || (e.touches && e.touches[0].clientX);
    const clientY = e.clientY || (e.touches && e.touches[0].clientY);

    const x = (clientX - rect.left) * 2;
    const y = (clientY - rect.top) * 2;

    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(x, y, 60, 0, Math.PI * 2); // Increased from 40 to 60 for more satisfying scratch
    ctx.fill();

    // Create more particles for satisfying effect
    if (Math.random() > 0.4) { // Increased frequency from 0.6 to 0.4
      const newParticle = {
        id: Date.now() + Math.random(),
        x: clientX - rect.left,
        y: clientY - rect.top,
        vx: (Math.random() - 0.5) * 5,
        vy: (Math.random() - 0.5) * 5 - 2,
        life: 1,
      };
      setParticles(prev => [...prev, newParticle]);
    }

    // Calculate progress
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    let transparent = 0;
    for (let i = 3; i < imageData.data.length; i += 4) {
      if (imageData.data[i] < 128) transparent++;
    }
    const progress = (transparent / (imageData.data.length / 4)) * 100;
    setScratchProgress(progress);

    if (progress > 60 && !isRevealed) {
      setIsRevealed(true);
      markAsScratched();
      generateConfetti();

      // Claim reward if credits
      if (reward.type === 'credits' && onRewardClaimed) {
        setTimeout(async () => {
          await onRewardClaimed(reward.amount);
        }, 1000);
      }
    }
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsScratching(true);
    scratch(e);
  };

  const handleMouseMove = (e) => {
    e.preventDefault();
    if (isScratching) scratch(e);
  };

  const handleMouseUp = () => {
    setIsScratching(false);
  };

  const handleTouchStart = (e) => {
    e.preventDefault();
    setIsScratching(true);
    scratch(e);
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    if (isScratching) scratch(e);
  };

  const handleTouchEnd = () => {
    setIsScratching(false);
  };

  const generateConfetti = () => {
    const colors = ['#FFD700', '#FF00FF', '#00FFFF', '#FF4444', '#00FF00', '#FFA500'];
    const newParticles = [];
    for (let i = 0; i < 80; i++) {
      newParticles.push({
        id: Date.now() + i,
        x: 200,
        y: 150,
        vx: (Math.random() - 0.5) * 15,
        vy: (Math.random() - 0.5) * 15 - 8,
        color: colors[Math.floor(Math.random() * colors.length)],
        life: 1,
        rotation: Math.random() * 360,
      });
    }
    setParticles(prev => [...prev, ...newParticles]);
  };

  useEffect(() => {
    if (particles.length === 0) return;

    const interval = setInterval(() => {
      setParticles(prev =>
        prev
          .map(p => ({
            ...p,
            x: p.x + p.vx,
            y: p.y + p.vy,
            vy: p.vy + 0.3,
            life: p.life - 0.015,
            rotation: (p.rotation || 0) + 8,
          }))
          .filter(p => p.life > 0)
      );
    }, 16);

    return () => clearInterval(interval);
  }, [particles]);

  if (!reward) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999]"
      onClick={onClose}
    >
      <div
        className="relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Particles */}
        {particles.map(p => (
          <div
            key={p.id}
            style={{
              position: 'absolute',
              left: `${p.x}px`,
              top: `${p.y}px`,
              width: '10px',
              height: '10px',
              background: p.color || style.border,
              borderRadius: '2px',
              opacity: p.life,
              transform: `rotate(${p.rotation}deg)`,
              pointerEvents: 'none',
            }}
          />
        ))}

        {/* Card */}
        <div
          style={{
            width: '400px',
            height: '300px',
            borderRadius: '20px',
            background: 'white',
            boxShadow: `${style.glow}, 0 10px 50px rgba(0, 0, 0, 0.5)`,
            border: `5px solid ${style.border}`,
            position: 'relative',
            overflow: 'hidden',
            cursor: isScratching ? 'grabbing' : 'grab',
            animation: style.animation || 'cardPulse 2s ease-in-out infinite',
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Revealed Content */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '30px',
              background: reward.rarity === 'dud' ? '#1a1a1a' : 'white',
              color: reward.rarity === 'dud' ? '#666' : '#000',
            }}
          >
            <div style={{ fontSize: '72px', marginBottom: '20px' }}>
              {reward.emoji}
            </div>
            <div style={{ fontSize: '28px', fontWeight: 'bold', textAlign: 'center', marginBottom: '10px' }}>
              {reward.label}
            </div>
            {reward.type === 'credits' && reward.rarity !== 'dud' && (
              <div style={{ fontSize: '16px', color: '#666', marginTop: '10px' }}>
                {isClaiming ? 'Adding to your account...' : 'Added to your account instantly! 🎉'}
              </div>
            )}
            {isRevealed && (
              <button
                onClick={onClose}
                style={{
                  marginTop: '20px',
                  padding: '12px 30px',
                  background: style.bg,
                  border: `3px solid ${style.border}`,
                  borderRadius: '12px',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '16px',
                  cursor: 'pointer',
                  boxShadow: style.glow,
                }}
              >
                {reward.rarity === 'dud' ? 'Try Again Tomorrow' : 'Awesome! 🎉'}
              </button>
            )}
          </div>

          {/* Scratch Canvas */}
          <canvas
            ref={canvasRef}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              cursor: isScratching ? 'grabbing' : 'grab',
              pointerEvents: isRevealed ? 'none' : 'auto',
            }}
          />

          {/* Scratch Hint */}
          {!isRevealed && scratchProgress < 5 && (
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                color: reward.rarity === 'legendary' ? 'white' : 'rgba(0, 0, 0, 0.4)',
                fontSize: '24px',
                fontWeight: 'bold',
                pointerEvents: 'none',
                textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                animation: 'bounce 2s ease-in-out infinite',
              }}
            >
              SCRATCH TO REVEAL! 👆
            </div>
          )}

          {/* Progress */}
          {!isRevealed && scratchProgress > 0 && (
            <div
              style={{
                position: 'absolute',
                bottom: '15px',
                left: '50%',
                transform: 'translateX(-50%)',
                fontSize: '14px',
                color: 'rgba(255, 255, 255, 0.9)',
                background: 'rgba(0, 0, 0, 0.6)',
                padding: '6px 12px',
                borderRadius: '20px',
                pointerEvents: 'none',
              }}
            >
              {Math.round(scratchProgress)}% revealed
            </div>
          )}
        </div>

        <style>{`
          @keyframes cardPulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.02); }
          }

          @keyframes rainbow {
            0% { filter: hue-rotate(0deg); }
            100% { filter: hue-rotate(360deg); }
          }

          @keyframes bounce {
            0%, 100% { transform: translate(-50%, -50%) translateY(0px); }
            50% { transform: translate(-50%, -50%) translateY(-10px); }
          }
        `}</style>
      </div>
    </div>
  );
};

// Main Black Friday Badge Component (Top Right Corner)
export const BlackFridayTheme = () => {
  const [showModal, setShowModal] = useState(false);
  const [hasScratched, setHasScratched] = useState(false);
  const [countdown, setCountdown] = useState('');
  const [isDismissed, setIsDismissed] = useState(false);
  const [showDismissMenu, setShowDismissMenu] = useState(false);
  const { themeEnabled } = useSeasonalTheme();
  const { user } = useAuth();

  useEffect(() => {
    setHasScratched(hasScatchedToday());
    setIsDismissed(isDismissedToday() || isPermanentlyDismissed());

    // Update countdown every minute
    const updateCountdown = () => {
      const countdownText = getCountdown();
      setCountdown(countdownText || 'Ended');
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  // Close dismiss menu when clicking outside
  useEffect(() => {
    if (!showDismissMenu) return;

    const handleClickOutside = (e) => {
      if (!e.target.closest('[data-dismiss-menu-container]')) {
        setShowDismissMenu(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showDismissMenu]);

  const handleRewardClaimed = async (credits) => {
    if (!user?.id) {
      console.error('No user ID available');
      return;
    }

    try {
      // Call API to add credits to user's account
      await manageUserCredits(user.id, 'topup', { amount: credits });
      console.log(`✅ Successfully added ${credits} credits to user account!`);

      // You could emit an event here to refresh user credits in the UI
      window.dispatchEvent(new CustomEvent('creditsUpdated'));
    } catch (error) {
      console.error('Failed to add credits:', error);
    }
  };

  const handleDismissForToday = () => {
    dismissForToday();
    setIsDismissed(true);
    setShowDismissMenu(false);
  };

  const handleDismissPermanently = () => {
    dismissPermanently();
    setIsDismissed(true);
    setShowDismissMenu(false);
  };

  if (!themeEnabled || isDismissed) return null;

  return (
    <>
      {/* Subtle Top Right Badge */}
      <div
        style={{
          position: 'fixed',
          top: '80px',
          right: '16px',
          zIndex: 999,
        }}
      >
        <div style={{ position: 'relative' }} data-dismiss-menu-container>
          <button
            onClick={() => !hasScratched && setShowModal(true)}
            disabled={hasScratched}
            title={hasScratched
              ? 'You\'ve already scratched today! Come back tomorrow for another chance to win free chat credits.'
              : 'Click to scratch and win free chat credits! One chance per day.'
            }
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '4px',
              padding: '10px 16px',
              paddingRight: '40px', // Make room for X button
              background: hasScratched
                ? 'rgba(100, 100, 100, 0.9)'
                : 'linear-gradient(135deg, #000000 0%, #FFD700 100%)',
              border: 'none',
              borderRadius: '12px',
              color: 'white',
              fontWeight: '600',
              fontSize: '13px',
              cursor: hasScratched ? 'not-allowed' : 'pointer',
              boxShadow: hasScratched
                ? '0 2px 8px rgba(0, 0, 0, 0.2)'
                : '0 2px 12px rgba(255, 215, 0, 0.4)',
              transition: 'all 0.3s ease',
              opacity: hasScratched ? 0.7 : 1,
              backdropFilter: 'blur(10px)',
            }}
            onMouseEnter={(e) => {
              if (!hasScratched) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(255, 215, 0, 0.6)';
              }
            }}
            onMouseLeave={(e) => {
              if (!hasScratched) {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 12px rgba(255, 215, 0, 0.4)';
              }
            }}
          >
            <div style={{ fontSize: '20px' }}>🎫</div>
            <div style={{ fontSize: '11px', fontWeight: '700' }}>
              {hasScratched ? 'Tomorrow!' : 'Scratch & Win'}
            </div>
            <div style={{ fontSize: '10px', opacity: 0.9, fontWeight: '500' }}>
              {countdown}
            </div>
          </button>

          {/* Dismiss Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowDismissMenu(!showDismissMenu);
            }}
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              width: '24px',
              height: '24px',
              background: 'rgba(0, 0, 0, 0.3)',
              border: 'none',
              borderRadius: '50%',
              color: 'white',
              fontSize: '14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              opacity: 0.7,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '1';
              e.currentTarget.style.background = 'rgba(0, 0, 0, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '0.7';
              e.currentTarget.style.background = 'rgba(0, 0, 0, 0.3)';
            }}
            title="Dismiss"
          >
            ×
          </button>

          {/* Dismiss Menu */}
          {showDismissMenu && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                right: '0',
                marginTop: '8px',
                background: 'white',
                borderRadius: '8px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                overflow: 'hidden',
                minWidth: '180px',
                zIndex: 1000,
              }}
            >
              <button
                onClick={handleDismissForToday}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: 'white',
                  border: 'none',
                  borderBottom: '1px solid #e5e5e5',
                  color: '#333',
                  fontSize: '13px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'background 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f5f5f5';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white';
                }}
              >
                <div style={{ fontWeight: '600', marginBottom: '4px' }}>Dismiss for today</div>
                <div style={{ fontSize: '11px', color: '#666' }}>Comes back tomorrow</div>
              </button>
              <button
                onClick={handleDismissPermanently}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: 'white',
                  border: 'none',
                  color: '#333',
                  fontSize: '13px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'background 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f5f5f5';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white';
                }}
              >
                <div style={{ fontWeight: '600', marginBottom: '4px' }}>Dismiss permanently</div>
                <div style={{ fontSize: '11px', color: '#666' }}>Won't show again</div>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Scratch Card Modal */}
      {showModal && (
        <ScratchCardModal
          onClose={() => {
            setShowModal(false);
            setHasScratched(true);
          }}
          onRewardClaimed={handleRewardClaimed}
        />
      )}
    </>
  );
};

// Black Friday Header (Chat header)
export const BlackFridayHeader = () => {
  const [countdown, setCountdown] = useState('');

  useEffect(() => {
    const updateCountdown = () => {
      const countdownText = getCountdown();
      setCountdown(countdownText || 'Ended');
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 60000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '6px 12px',
        background: 'linear-gradient(135deg, #000000 0%, #FFD700 100%)',
        borderRadius: '16px',
        color: 'white',
        fontWeight: '600',
        fontSize: '12px',
      }}
    >
      <span style={{ fontSize: '16px' }}>🎫</span>
      <span>Black Friday {countdown}</span>
    </div>
  );
};

export default BlackFridayTheme;
